import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { Checkbox } from "../../components/ui/checkbox";
import { User, Key, Check, Loader2, AlertCircle, ScrollText } from "lucide-react";
import { PhoneInput } from "../../components/ui/phone-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import { OTPInput as InputOTP } from "input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../api/client";
import { toast } from "../../hooks/use-toast";
import { translateBackendError } from "../../utils/errors";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";

export default function Register() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        nom: "",
        prenom: "",
        email: "",
        phone: "",
        pin: "",
        confirmPin: "",
    });

    const [acceptTerms, setAcceptTerms] = useState(false);
    const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
    const [isPinEntered, setIsPinEntered] = useState(false);
    const [isTermsOpen, setIsTermsOpen] = useState(false);
    const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
    const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);

    const [errors, setErrors] = useState<Record<string, string>>({});

    const TERMS_CONTENT = (
        <div className="space-y-4 text-sm text-gray-600 max-h-[60vh] overflow-y-auto pr-2">
            <section>
                <h3 className="font-semibold text-gray-900 mb-2">{t('auth:terms.sections.title1')}</h3>
                <p>{t('auth:terms.sections.content1')}</p>
            </section>

            <section>
                <h3 className="font-semibold text-gray-900 mb-2">{t('auth:terms.sections.title2')}</h3>
                <p>{t('auth:terms.sections.content2')}</p>
            </section>

            <section>
                <h3 className="font-semibold text-gray-900 mb-2">{t('auth:terms.sections.title3')}</h3>
                <p>{t('auth:terms.sections.content3')}</p>
            </section>

            <section>
                <h3 className="font-semibold text-gray-900 mb-2">{t('auth:terms.sections.title4')}</h3>
                <p>{t('auth:terms.sections.content4')}</p>
            </section>

            <section>
                <h3 className="font-semibold text-gray-900 mb-2">{t('auth:terms.sections.title5')}</h3>
                <p>{t('auth:terms.sections.content5')}</p>
            </section>
        </div>
    );

    // Handle Terms Acceptance Animation
    const handleAcceptTerms = async () => {
        setIsAcceptingTerms(true);
        // Simulate a brief processing time for better UX (500ms)
        await new Promise(resolve => setTimeout(resolve, 500));

        setAcceptTerms(true);
        setIsAcceptingTerms(false);
        setIsTermsOpen(false);
        setIsPrivacyOpen(false);
    };

    // Clear API error when form data changes
    useEffect(() => {
        if (apiError) setApiError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.email, formData.phone, formData.nom, formData.prenom, formData.pin, formData.confirmPin]);

    // Handle PIN entry completion
    useEffect(() => {
        if (formData.pin.length === 4) {
            // Delay slightly to let user see the last digit
            const timer = setTimeout(() => {
                setIsPinEntered(true);
            }, 300);
            return () => clearTimeout(timer);
        } else {
            setIsPinEntered(false);
        }
    }, [formData.pin]);

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};

        if (step === 1) {
            if (!formData.nom.trim()) newErrors.nom = t('auth:signup.validation.nameRequired');
            if (!formData.prenom.trim()) newErrors.prenom = t('auth:signup.validation.firstNameRequired');
            if (!formData.email.trim()) {
                newErrors.email = t('auth:signup.validation.emailRequired');
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                newErrors.email = t('auth:signup.validation.emailInvalid');
            }
            if (!formData.phone) {
                newErrors.phone = t('auth:signup.validation.phoneRequired');
            } else if (!isValidPhoneNumber(formData.phone)) {
                newErrors.phone = t('auth:signup.validation.phoneInvalid');
            }
        } else if (step === 2) {
            if (formData.pin.length !== 4) {
                newErrors.pin = t('auth:signup.validation.pin4Digits');
            }
            if (formData.pin !== formData.confirmPin) {
                newErrors.confirmPin = t('auth:signup.validation.pinMismatch');
            }
            if (!acceptTerms) {
                newErrors.terms = t('auth:signup.validation.termsRequired') || "You must accept terms"; // Fallback if key missing
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const checkAvailability = async (): Promise<boolean> => {
        try {
            const { data } = await apiClient.post('/api/auth/check-availability', {
                email: formData.email,
                telephone: formData.phone,
            });

            if (!data.available) {
                if (data.field === 'email') {
                    setErrors(prev => ({ ...prev, email: translateBackendError(data.message, i18n.language) }));
                } else if (data.field === 'telephone') {
                    setErrors(prev => ({ ...prev, phone: translateBackendError(data.message, i18n.language) }));
                }
                return false;
            }
            return true;
        } catch (error: any) {
            const rawMsg = error.response?.data?.message || error.message || t('common:errors.unknown');
            const msg = translateBackendError(rawMsg, i18n.language);
            setApiError(msg);
            return false;
        }
    };

    const handleNext = async () => {
        if (!validateStep(1)) return;

        setIsLoading(true);
        const isAvailable = await checkAvailability();
        setIsLoading(false);

        if (isAvailable) {
            setErrors({});
            setSlideDirection('right');
            setCurrentStep(2);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (currentStep === 1) {
            await handleNext();
            return;
        }

        if (!validateStep(2)) return;

        setIsLoading(true);
        setApiError(null);

        try {
            await apiClient.post('/api/clients/register', {
                nom: formData.nom,
                prenom: formData.prenom,
                email: formData.email,
                telephone: formData.phone,
                pin: formData.pin,
                frontendUrl: window.location.origin,
            }, {
                timeout: 60000, // 60 seconds - registration creates multiple entities
            });

            // Success - show toast and redirect to login
            toast({
                title: "🎉 " + t('auth:success.signupSuccess') + "!",
                description: t('auth:signup.toast.registrationSuccess'),
                type: "success",
                duration: 6000, // Show longer
            });

            navigate('/login', {
                state: { message: t('auth:signup.toast.registrationSuccess') }
            });
        } catch (error: any) {
            const rawMsg = error.response?.data?.message || error.message || t('auth:signup.toast.registrationError');
            setApiError(translateBackendError(rawMsg, i18n.language));
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        if (currentStep === 2 && isPinEntered) {
            setIsPinEntered(false);
            setFormData(prev => ({ ...prev, pin: "", confirmPin: "" })); // Reset PINs to force re-entry if going back
            return;
        }

        setErrors({});
        setApiError(null);
        setSlideDirection('left');
        setCurrentStep(1);
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-100 to-gray-100 p-4">
            <Card className="w-full max-w-md shadow-2xl bg-white/90 backdrop-blur-md">
                <CardHeader className="flex flex-col items-center text-center space-y-4 pt-6">
                    <div className="bg-primary-50 p-2 rounded-full">
                        <a href="https://bousanm.com">
                            <img src="/logo.webp" alt="Logo" className="w-12 h-12 object-contain" />
                        </a>
                    </div>
                    <h1 className="text-xl font-bold text-gray-900">{t('auth:signup.title')}</h1>

                    {/* Step Indicator */}
                    <div className="w-full flex items-center justify-center gap-4">
                        <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${currentStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'
                                }`}>
                                {currentStep > 1 ? <Check size={20} /> : <User size={20} />}
                            </div>
                            <span className="text-xs mt-1 text-gray-600">{t('auth:signup.steps.info')}</span>
                        </div>

                        <div className={`flex-1 h-0.5 ${currentStep > 1 ? 'bg-primary-600' : 'bg-gray-300'}`} />

                        <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${currentStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'
                                }`}>
                                <Key size={20} />
                            </div>
                            <span className="text-xs mt-1 text-gray-600">{t('auth:signup.steps.pin')}</span>
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="pt-4">
                    {apiError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                            <AlertCircle size={18} />
                            {apiError}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Step 1: Personal Information */}
                        {currentStep === 1 && (
                            <div
                                className={`space-y-4 transition-all duration-300 ease-out ${slideDirection === 'left'
                                    ? 'animate-in slide-in-from-left-8 fade-in'
                                    : 'animate-in fade-in'
                                    }`}
                            >
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="prenom">{t('auth:signup.personalInfo.firstName')}</Label>
                                        <Input
                                            id="prenom"
                                            type="text"
                                            placeholder={t('auth:signup.personalInfo.firstName')}
                                            className={errors.prenom ? "border-red-500" : ""}
                                            value={formData.prenom}
                                            onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                                        />
                                        {errors.prenom && <p className="text-red-500 text-xs">{errors.prenom}</p>}
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="nom">{t('auth:signup.personalInfo.lastName')}</Label>
                                        <Input
                                            id="nom"
                                            type="text"
                                            placeholder={t('auth:signup.personalInfo.lastName')}
                                            className={errors.nom ? "border-red-500" : ""}
                                            value={formData.nom}
                                            onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                        />
                                        {errors.nom && <p className="text-red-500 text-xs">{errors.nom}</p>}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">{t('auth:signup.personalInfo.email')}</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder={t('auth:login.emailPlaceholder')}
                                        className={errors.email ? "border-red-500" : ""}
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    />
                                    {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="phone">{t('auth:signup.personalInfo.phone')}</Label>
                                    <PhoneInput
                                        id="phone"
                                        placeholder={t('auth:signup.personalInfo.phone')}
                                        value={formData.phone}
                                        onChange={(value: string | undefined) => setFormData({ ...formData, phone: value || "" })}
                                        defaultCountry="HT"
                                        className={errors.phone ? "border-red-500" : ""}
                                    />
                                    {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
                                </div>
                            </div>
                        )}

                        {/* Step 2: PIN Configuration */}
                        {currentStep === 2 && (
                            <div className="space-y-6 overflow-hidden">
                                <div className="text-center mb-6">
                                    <p className="text-sm text-gray-500">
                                        {t('auth:signup.pinSetup.subtitle')}
                                    </p>
                                </div>

                                {/* PIN Entry Container - Grid Stack */}
                                <div className="grid grid-cols-1">
                                    {/* First PIN Input - Slides Left when done */}
                                    <div
                                        className={`col-start-1 row-start-1 w-full transition-all duration-500 ease-in-out transform ${isPinEntered ? '-translate-x-full opacity-0 pointer-events-none' : 'translate-x-0 opacity-100'
                                            }`}
                                    >
                                        <div className="flex flex-col items-center space-y-6">
                                            <div className="space-y-2 text-center">
                                                <Label className="text-gray-700 font-semibold">{t('auth:signup.pinSetup.pinLabel')}</Label>
                                                <InputOTP
                                                    maxLength={4}
                                                    value={formData.pin}
                                                    onChange={(value: string) => setFormData({ ...formData, pin: value })}
                                                    containerClassName="flex justify-center"
                                                    pattern={REGEXP_ONLY_DIGITS}
                                                    render={({ slots }: { slots: any[] }) => (
                                                        <div className="flex gap-3">
                                                            {slots.map((slot: any, idx: number) => (
                                                                <div
                                                                    key={idx}
                                                                    className={`w-12 h-12 flex items-center justify-center text-xl font-bold bg-white rounded-xl shadow-sm transition-all ${slot.isActive ? 'border-2 border-primary ring-2 ring-primary/20' : 'border border-gray-200'
                                                                        }`}
                                                                >
                                                                    {slot.char ? <div className="w-3 h-3 rounded-full bg-primary" /> : null}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                />
                                                {errors.pin && <p className="text-red-500 text-xs">{errors.pin}</p>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Confirm PIN Input - Slides in from Right */}
                                    <div
                                        className={`col-start-1 row-start-1 w-full transition-all duration-500 ease-in-out transform ${isPinEntered ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
                                            }`}
                                    >
                                        <div className="flex flex-col items-center space-y-6">
                                            <div className="space-y-2 text-center">
                                                <Label className="text-gray-700 font-semibold">{t('auth:signup.pinSetup.confirmPinLabel')}</Label>
                                                <InputOTP
                                                    maxLength={4}
                                                    value={formData.confirmPin}
                                                    onChange={(value: string) => setFormData({ ...formData, confirmPin: value })}
                                                    containerClassName="flex justify-center"
                                                    pattern={REGEXP_ONLY_DIGITS}
                                                    autoFocus={isPinEntered} // Auto focus when visible
                                                    render={({ slots }: { slots: any[] }) => (
                                                        <div className="flex gap-3">
                                                            {slots.map((slot: any, idx: number) => (
                                                                <div
                                                                    key={idx}
                                                                    className={`w-12 h-12 flex items-center justify-center text-xl font-bold bg-white rounded-xl shadow-sm transition-all ${slot.isActive ? 'border-2 border-primary ring-2 ring-primary/20' :
                                                                        formData.confirmPin.length === 4 && formData.pin !== formData.confirmPin ? 'border-2 border-red-500' :
                                                                            formData.confirmPin.length === 4 && formData.pin === formData.confirmPin ? 'border-2 border-green-500' :
                                                                                'border border-gray-200'
                                                                        }`}
                                                                >
                                                                    {slot.char ? (
                                                                        <div className={`w-3 h-3 rounded-full ${formData.confirmPin.length === 4 && formData.pin !== formData.confirmPin ? 'bg-red-500' :
                                                                            formData.confirmPin.length === 4 && formData.pin === formData.confirmPin ? 'bg-green-500' :
                                                                                'bg-primary'
                                                                            }`} />
                                                                    ) : null}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                />
                                                {errors.confirmPin && <p className="text-red-500 text-xs">{errors.confirmPin}</p>}
                                            </div>

                                            {/* Terms Checkbox - Only show when confirming PIN */}
                                            <div className="flex items-start space-x-3 pt-4 animate-in fade-in zoom-in duration-500 delay-300">
                                                <Checkbox
                                                    id="terms"
                                                    checked={acceptTerms}
                                                    onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                                                    className="mt-0.5"
                                                />
                                                <div className="grid gap-1.5 leading-none">
                                                    <label
                                                        htmlFor="terms"
                                                        className="text-sm text-gray-600 cursor-pointer"
                                                    >
                                                        {t('auth:signup.buttons.alreadyRegistered').split('?')[0]} {/* This logic is a bit fragile, but works for "Mwen aksepte" part */}
                                                        {t('common:iAccept') || "I accept"}{" "}
                                                        <Dialog open={isTermsOpen} onOpenChange={setIsTermsOpen}>
                                                            <DialogTrigger className="text-primary-600 hover:underline">
                                                                {t('auth:terms.title')}
                                                            </DialogTrigger>
                                                            <DialogContent className="sm:max-w-[425px]">
                                                                <DialogHeader>
                                                                    <DialogTitle className="flex items-center gap-2">
                                                                        <ScrollText className="w-5 h-5 text-primary" />
                                                                        {t('auth:terms.title')}
                                                                    </DialogTitle>
                                                                    <DialogDescription>
                                                                        {t('auth:terms.description')}
                                                                    </DialogDescription>
                                                                </DialogHeader>
                                                                {TERMS_CONTENT}
                                                                <Separator className="my-2" />
                                                                <div className="flex justify-end">
                                                                    <Button
                                                                        onClick={handleAcceptTerms}
                                                                        type="button"
                                                                        className={`w-full sm:w-auto transition-all duration-300 ${isAcceptingTerms ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                                                        disabled={isAcceptingTerms}
                                                                    >
                                                                        {isAcceptingTerms ? (
                                                                            <>
                                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                                {t('auth:terms.accepting')}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Check className="w-4 h-4 mr-2" />
                                                                                {t('auth:terms.accept')}
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                </div>
                                                            </DialogContent>
                                                        </Dialog>
                                                        {" "}{t('common:and') || "and"}{" "}
                                                        <Dialog open={isPrivacyOpen} onOpenChange={setIsPrivacyOpen}>
                                                            <DialogTrigger className="text-primary-600 hover:underline">
                                                                {t('auth:privacy.title')}
                                                            </DialogTrigger>
                                                            <DialogContent className="sm:max-w-[425px]">
                                                                <DialogHeader>
                                                                    <DialogTitle>{t('auth:privacy.title')}</DialogTitle>
                                                                </DialogHeader>
                                                                {TERMS_CONTENT}
                                                                <Separator className="my-2" />
                                                                <div className="flex justify-end">
                                                                    <Button
                                                                        onClick={handleAcceptTerms}
                                                                        type="button"
                                                                        className={`w-full sm:w-auto transition-all duration-300 ${isAcceptingTerms ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                                                        disabled={isAcceptingTerms}
                                                                    >
                                                                        {isAcceptingTerms ? (
                                                                            <>
                                                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                                {t('auth:terms.accepting')}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Check className="w-4 h-4 mr-2" />
                                                                                {t('auth:terms.accept')}
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                </div>
                                                            </DialogContent>
                                                        </Dialog>
                                                    </label>
                                                    {errors.terms && (
                                                        <p className="text-red-500 text-xs">{errors.terms}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <Separator className="my-6" />

                        <div className="flex gap-4">
                            {currentStep > 1 && (
                                <Button type="button" variant="outline" onClick={handleBack} className="flex-1" disabled={isLoading}>
                                    {t('common:buttons.back')}
                                </Button>
                            )}
                            <Button
                                type="submit"
                                className="flex-1"
                                disabled={isLoading || (currentStep === 2 && !acceptTerms)}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {t('common:loading')}
                                    </>
                                ) : currentStep < 2 ? (
                                    t('auth:signup.buttons.next')
                                ) : (
                                    t('auth:signup.createAccount')
                                )}
                            </Button>
                        </div>
                    </form>
                </CardContent>

                <Separator />
                <CardFooter className="flex flex-col gap-3 pt-4">
                    <div className="text-center text-sm">
                        <span className="text-gray-600">{t('auth:signup.buttons.alreadyRegistered').split('?')[0]}? </span>
                        <a href="/login" className="text-primary-600 hover:text-primary-700 hover:underline font-semibold">
                            {t('auth:login.loginButton')}
                        </a>
                    </div>
                </CardFooter>
            </Card>
        </div>
    );
}

